import React, { useContext, useEffect, useState } from 'react';
import { AuthContext } from '../../context/AuthContext';
import { FaCar, FaMotorcycle, FaUsers } from 'react-icons/fa';
import useApi from '../../hooks/useApi';
import Alert from '../common/Alert';
import CommunityIdentity from '../common/CommunityIdentity';
import 'animate.css';

const MetricCard = ({ icon, label, value, help }) => (
  <div className="col-xl-3 col-md-4 col-sm-6">
    <div className="card border-0 shadow-sm h-100">
      <div className="card-body">
        <div className="d-flex align-items-start gap-3">
          <span className="text-primary mt-1">{icon}</span>
          <div>
            <div className="fw-semibold">{label}</div>
            <div className="fs-4 fw-bold">{value ?? 0}</div>
            <small className="text-muted">{help}</small>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const MetricSection = ({ title, description, children }) => (
  <section className="mb-4">
    <h3 className="fs-5 mb-1">{title}</h3>
    <p className="text-muted mb-3">{description}</p>
    <div className="row g-3">{children}</div>
  </section>
);

function UserAnalytics() {
  const { session } = useContext(AuthContext);
  const { roles, communityId } = session || { roles: [], communityId: null };
  const isSuperAdmin = roles.includes('SUPER_ADMIN');
  const isAdmin = roles.includes('ADMIN');
  const { get } = useApi();
  const [analytics, setAnalytics] = useState(null);
  const [communities, setCommunities] = useState([]);
  const [selectedCommunityId, setSelectedCommunityId] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const communityRequest = isSuperAdmin
          ? get('/api/communities/active')
          : Promise.resolve([]);
        const analyticsRequest = get('/api/users/analytics', {
          params: isSuperAdmin
            ? selectedCommunityId
              ? { communityId: selectedCommunityId }
              : {}
            : { communityId },
        });
        const [communityResult, analyticsResult] = await Promise.all([
          communityRequest,
          analyticsRequest,
        ]);
        if (!cancelled) {
          setCommunities(Array.isArray(communityResult) ? communityResult : []);
          setAnalytics(analyticsResult);
          setError('');
        }
      } catch (requestError) {
        if (!cancelled)
          setError(`Unable to load user analytics: ${requestError.message}`);
      }
    };
    if (isAdmin || isSuperAdmin) load();
    return () => {
      cancelled = true;
    };
  }, [get, isAdmin, isSuperAdmin, communityId, selectedCommunityId]);

  if (!isAdmin && !isSuperAdmin)
    return (
      <Alert
        type="warning"
        message="Only Community Admin and Super Admin can view user analytics."
      />
    );
  if (!analytics && !error)
    return <div className="text-center py-5">Loading user analytics…</div>;

  const selectedCommunity = communities.find(
    (community) => String(community.id) === String(selectedCommunityId)
  );
  const analyticsCommunity = selectedCommunity || (!isSuperAdmin ? session?.community : null);

  return (
    <div className="container py-3 animate__animated animate__fadeIn">
      <div className="mb-4">
        <h2 className="fs-4 fw-bold mb-1">Community Analytics</h2>
        <p className="text-muted mb-0">
          Account, access-role, resident profile and vehicle indicators are
          separated below so overlapping categories are not mistaken for totals.
        </p>
      </div>
      {error && <Alert type="danger" message={error} />}
      {isSuperAdmin && (
        <label className="mb-4 w-100">
          Community
          <select
            className="form-select"
            value={selectedCommunityId}
            onChange={(event) => setSelectedCommunityId(event.target.value)}
          >
            <option value="">All communities</option>
            {communities.map((community) => (
              <option key={community.id} value={community.id}>
                {community.displayName}
              </option>
            ))}
          </select>
        </label>
      )}
      {analytics && (
        <>
          <h3 className="fs-4 mb-4">
            {analyticsCommunity ? (
              <CommunityIdentity community={analyticsCommunity} size="sm" />
            ) : (
              isSuperAdmin ? 'All communities' : 'Your community'
            )}
          </h3>
          <MetricSection
            title="Account overview"
            description="These are account lifecycle statuses. Each registered account appears in one status only."
          >
            <MetricCard icon={<FaUsers />} label="Total accounts" value={analytics.totalRegisteredUsers} help="Unique registered user accounts." />
            <MetricCard icon={<FaUsers />} label="Pending approval" value={analytics.pendingUsers} help="Waiting for an administrator decision." />
            <MetricCard icon={<FaUsers />} label="Approved, not yet signed in" value={analytics.approvedUsers} help="Approved accounts become active on their next successful login." />
            <MetricCard icon={<FaUsers />} label="Active accounts" value={analytics.activeUsers} help="Approved accounts that have signed in." />
            <MetricCard icon={<FaUsers />} label="Disabled accounts" value={analytics.disabledUsers} help="Accounts currently prevented from signing in." />
          </MetricSection>

          <MetricSection
            title="Access roles"
            description="Role totals can overlap because one account may be both a resident and an administrator or security user."
          >
            <MetricCard icon={<FaUsers />} label="Resident accounts" value={analytics.residentUsers} help="Accounts holding the Resident/User role." />
            <MetricCard icon={<FaUsers />} label="Community administrators" value={analytics.adminUsers} help="Accounts holding the Community Admin role." />
            <MetricCard icon={<FaUsers />} label="Security staff" value={analytics.securityUsers} help="Accounts holding the Security role." />
          </MetricSection>

          <MetricSection
            title="Resident status"
            description="These figures include only accounts holding the Resident/User role. Each resident account appears in one lifecycle status."
          >
            <MetricCard icon={<FaUsers />} label="Total residents" value={analytics.residentUsers} help="All accounts holding the Resident/User role." />
            <MetricCard icon={<FaUsers />} label="Active residents" value={analytics.activeResidents} help="Residents approved and actively using the application." />
            <MetricCard icon={<FaUsers />} label="Awaiting approval" value={analytics.pendingResidents} help="Resident registrations awaiting an administrator decision." />
            <MetricCard icon={<FaUsers />} label="Approved, not yet active" value={analytics.approvedResidents} help="Approved residents who have not signed in since approval." />
            <MetricCard icon={<FaUsers />} label="Inactive / disabled" value={analytics.inactiveResidents} help="Resident accounts currently prevented from signing in." />
          </MetricSection>

          <MetricSection
            title="Home configuration health"
            description="A mapped resident has at least one active canonical home assignment. Unmapped residents should be reviewed in Homes & Residents before generating property-based bills."
          >
            <MetricCard icon={<FaUsers />} label="Mapped residents" value={analytics.mappedResidents} help="Residents linked to at least one configured property." />
            <MetricCard icon={<FaUsers />} label="Unmapped residents" value={analytics.unmappedResidents} help="Resident accounts without an active canonical property mapping." />
          </MetricSection>

          <section className="mb-4">
            <div className="d-flex flex-wrap justify-content-between align-items-end gap-2 mb-3">
              <div>
                <h3 className="fs-5 mb-1">Residents by configured location</h3>
                <p className="text-muted mb-0">
                  Counts follow the canonical division, wing, block or tower
                  assigned in Homes &amp; Residents. Unmapped accounts are shown
                  separately.
                </p>
              </div>
            </div>
            <div className="table-responsive card border-0 shadow-sm">
              <table className="table table-hover align-middle mb-0">
                <thead>
                  <tr>
                    {isSuperAdmin && !selectedCommunityId && <th>Community</th>}
                    <th>Division / wing / block</th>
                    <th>Mapping</th>
                    <th className="text-end">Total</th>
                    <th className="text-end">Active</th>
                    <th className="text-end">Approved, not active</th>
                    <th className="text-end">Pending</th>
                    <th className="text-end">Inactive</th>
                  </tr>
                </thead>
                <tbody>
                  {(analytics.residentClassifications || []).map((row, index) => (
                    <tr key={`${row.communityId}-${row.classification}-${row.mappingStatus}-${index}`}>
                      {isSuperAdmin && !selectedCommunityId && (
                        <td>
                          <CommunityIdentity
                            community={communities.find(community => String(community.id) === String(row.communityId)) || { name: row.communityName }}
                            size="xs"
                            compact
                          />
                        </td>
                      )}
                      <td className="fw-semibold">{row.classification}</td>
                      <td>
                        <span className={`badge ${row.mappingStatus === 'MAPPED' ? 'text-bg-success' : 'text-bg-warning'}`}>
                          {row.mappingStatus}
                        </span>
                      </td>
                      <td className="text-end fw-semibold">{row.totalResidents}</td>
                      <td className="text-end">{row.activeResidents}</td>
                      <td className="text-end">{row.approvedResidents}</td>
                      <td className="text-end">{row.pendingResidents}</td>
                      <td className="text-end">{row.inactiveResidents}</td>
                    </tr>
                  ))}
                  {!analytics.residentClassifications?.length && (
                    <tr>
                      <td colSpan={isSuperAdmin && !selectedCommunityId ? 8 : 7} className="text-center text-muted py-4">
                        No resident classification data is available.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <MetricSection
            title="Resident profile"
            description="Household members are people recorded under resident accounts and do not necessarily have their own login. Owner and tenant values come from resident account profiles."
          >
            <MetricCard icon={<FaUsers />} label="Total household members" value={analytics.totalMembers} help="All people recorded in household member lists; this is separate from login accounts." />
            <MetricCard icon={<FaUsers />} label="Owner profiles" value={analytics.owners} help="Resident profiles classified as owner." />
            <MetricCard icon={<FaUsers />} label="Tenant profiles" value={analytics.tenants} help="Resident profiles classified as tenant." />
          </MetricSection>

          <MetricSection
            title="Vehicle indicators"
            description="These are exception counts for residents exceeding the configured vehicle thresholds."
          >
            <MetricCard
              icon={<FaMotorcycle />}
              label="More than 2 two-wheelers"
              value={(analytics.usersWithMoreThan2TwoWheelersOwners || 0) + (analytics.usersWithMoreThan2TwoWheelersTenants || 0)}
              help={`Owners: ${analytics.usersWithMoreThan2TwoWheelersOwners || 0}; tenants: ${analytics.usersWithMoreThan2TwoWheelersTenants || 0}.`}
            />
            <MetricCard
              icon={<FaCar />}
              label="More than 1 four-wheeler"
              value={(analytics.usersWithMoreThan2FourWheelersOwners || 0) + (analytics.usersWithMoreThan2FourWheelersTenants || 0)}
              help={`Owners: ${analytics.usersWithMoreThan2FourWheelersOwners || 0}; tenants: ${analytics.usersWithMoreThan2FourWheelersTenants || 0}.`}
            />
          </MetricSection>
        </>
      )}
    </div>
  );
}

export default UserAnalytics;
